import * as React from 'react'

import { Button } from '~/client/src/shared/components/Button/Button'
import { CloseIcon } from '~/client/src/shared/components/icons/CloseIcon'
import { DownloadIcon } from '~/client/src/shared/components/icons/DownloadIcon'
import { PrintIcon } from '~/client/src/shared/components/icons/PrintIcon'
import { ShareIcon } from '~/client/src/shared/components/icons/ShareIcon'
import { QRCode } from '~/client/src/shared/components/LazyQRCode'
import { ReactToPrint } from '~/client/src/shared/components/LazyReactToPrint'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ThemeMode from '~/client/src/shared/utils/ThemeModeManager'

import Colors from '~/client/src/shared/theme.module.scss'

const QR_CODE_SIZE = 216
const QR_CODE_LOGO_SIZE = 60
const QR_CODE_TO_PRINT_SIZE = 700
const QR_CODE_LOGO_SRC = '/static/icons/strux-hub-dark-with-background.svg'
const SIGNUP_QR_CANVAS_ID = 'signup-qr-code'

interface ISignUpQRContextValue {
  state: { signUpUrl: string; projectName: string }
  actions: {
    copyLink: () => void
    download: () => void
    openShare: () => void
    closeShare: () => void
  }
  meta: {
    canvasId: string
    printRef: React.RefObject<HTMLDivElement>
    isShareOpen: boolean
  }
}

const SignUpQRContext = React.createContext<ISignUpQRContextValue | null>(null)

function useSignUpQR(): ISignUpQRContextValue {
  const ctx = React.useContext(SignUpQRContext)
  if (!ctx) {
    throw new Error('useSignUpQR must be used within SignUpQR.Provider')
  }
  return ctx
}

interface IProviderProps {
  signUpUrl: string
  projectName: string
  onCopyLink: () => void
  children: React.ReactNode
}

function SignUpQRProvider({
  signUpUrl,
  projectName,
  onCopyLink,
  children,
}: IProviderProps) {
  const printRef = React.useRef<HTMLDivElement>(null)
  const [isShareOpen, setIsShareOpen] = React.useState(false)

  const download = React.useCallback(() => {
    const canvas = document.getElementById(
      SIGNUP_QR_CANVAS_ID,
    ) as HTMLCanvasElement
    if (!canvas) {
      return
    }
    const url = canvas.toDataURL('image/png')
    const link = document.createElement('a')
    link.download = 'sign-up-qr-code.png'
    link.href = url
    link.click()
  }, [])

  const value = React.useMemo<ISignUpQRContextValue>(
    () => ({
      state: { signUpUrl, projectName },
      actions: {
        copyLink: onCopyLink,
        download,
        openShare: () => setIsShareOpen(true),
        closeShare: () => setIsShareOpen(false),
      },
      meta: {
        canvasId: SIGNUP_QR_CANVAS_ID,
        printRef,
        isShareOpen,
      },
    }),
    [signUpUrl, projectName, onCopyLink, download, isShareOpen],
  )

  return (
    <SignUpQRContext.Provider value={value}>
      {children}
    </SignUpQRContext.Provider>
  )
}

function SignUpQRCode() {
  const { state, meta } = useSignUpQR()
  return (
    <QRCode
      id={meta.canvasId}
      value={state.signUpUrl}
      logoImage={QR_CODE_LOGO_SRC}
      logoWidth={QR_CODE_LOGO_SIZE}
      logoHeight={QR_CODE_LOGO_SIZE}
      size={QR_CODE_SIZE}
      fgColor={ThemeMode.getHEXColor(Colors.neutral0)}
    />
  )
}

function SignUpQRPrintButton() {
  const { meta } = useSignUpQR()
  return (
    <ReactToPrint
      trigger={() => (
        <Button variant="outline" size="lg">
          <PrintIcon data-icon="inline-start" size="xs" />
          {Localization.translator.print}
        </Button>
      )}
      content={() => meta.printRef.current}
    />
  )
}

function SignUpQRShareButton() {
  const { actions } = useSignUpQR()
  return (
    <Button variant="outline" size="lg" onClick={actions.openShare}>
      <ShareIcon data-icon="inline-start" size="xs" />
      {Localization.translator.share}
    </Button>
  )
}

function SignUpQRDownloadButton() {
  const { actions } = useSignUpQR()
  return (
    <Button variant="default" size="lg" onClick={actions.download}>
      <DownloadIcon data-icon="inline-start" size="xs" />
      {Localization.translator.download}
    </Button>
  )
}

function SignUpQRShareDialog() {
  const { state, actions, meta } = useSignUpQR()
  return (
    <Dialog
      open={meta.isShareOpen}
      onOpenChange={open => {
        if (!open) {
          actions.closeShare()
        }
      }}
    >
      <DialogContent className="tw:w-[520px]" showCloseButton={false}>
        <DialogHeader className="tw:flex-row tw:items-center tw:justify-between">
          <DialogTitle>
            {Localization.translator.share} {state.projectName}
          </DialogTitle>
          <DialogClose className="tw:inline-flex tw:size-8 tw:items-center tw:justify-center tw:rounded-md tw:border-0 tw:bg-transparent tw:text-muted-foreground tw:outline-none tw:transition-colors tw:hover:bg-muted tw:hover:text-foreground tw:focus-visible:border-ring tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50">
            <CloseIcon size="sm" />
          </DialogClose>
        </DialogHeader>
        <DialogBody>
          <div className="tw:flex tw:flex-col tw:gap-2">
            <span className="tw:text-body-sm tw:text-muted-foreground">
              {Localization.translator.shareWithLink}
            </span>
            <div className="tw:flex tw:gap-2 tw:items-start">
              <div className="tw:flex-1">
                <StruxhubInput
                  isRequiredTextHidden={true}
                  value={state.signUpUrl}
                  isReadOnly={true}
                  onChange={undefined}
                />
              </div>
              <Button
                variant="default"
                size="lg"
                className="tw:mt-2"
                onClick={actions.copyLink}
              >
                {Localization.translator.copyLink}
              </Button>
            </div>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

function SignUpQRPrintTarget() {
  const { state, meta } = useSignUpQR()
  return (
    <div className="tw:hidden">
      <div
        ref={meta.printRef}
        className="tw:flex tw:flex-col tw:items-center tw:p-8"
      >
        <QRCode
          value={state.signUpUrl}
          logoImage={QR_CODE_LOGO_SRC}
          size={QR_CODE_TO_PRINT_SIZE}
          fgColor={ThemeMode.getHEXColor(Colors.neutral0)}
        />
        <p className="tw:text-2xl tw:font-bold tw:text-center tw:mt-4">
          {Localization.translator.signUp}
        </p>
      </div>
    </div>
  )
}

export const SignUpQR = {
  Provider: SignUpQRProvider,
  Code: SignUpQRCode,
  PrintButton: SignUpQRPrintButton,
  ShareButton: SignUpQRShareButton,
  DownloadButton: SignUpQRDownloadButton,
  ShareDialog: SignUpQRShareDialog,
  PrintTarget: SignUpQRPrintTarget,
}
